import { salesPartnerSettings, userManagement } from '@e2m-customer-portal/core';
import {
  useAuth,
  useEnvSettings,
  useFeatureFlags,
  usePermissions,
  useSalesPartner,
  useSalesPartners,
  useTokenInfo,
} from '@e2m-customer-portal/hooks';
import { PermissionId, UserRole } from '@e2m-customer-portal/shared';
import { Menu, MenuItem, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ArrowRightIcon, LogoutIcon, ProfileIcon } from '../../assets/icons';
import { isGranted as checkGranted } from '../../utils';

export const HeaderUserSettings: React.FC = () => {
  const [t] = useTranslation();
  const { logout } = useAuth();
  const { email, roles } = useTokenInfo();
  const { permissions } = usePermissions();
  const { useNewSalesPartnerAPI } = useFeatureFlags();
  const salesPartner = useNewSalesPartnerAPI ? undefined : useSalesPartner();
  const salesPartners = useNewSalesPartnerAPI && roles.has(UserRole.PlantOperator) ? useSalesPartners() : [];
  const { operationsPortalURL, billingPortalURL, tradingPortalURL, marketingPortalURL } = useEnvSettings();
  const portalLinks = [
    { key: 'operations', to: operationsPortalURL },
    { key: 'billing', to: billingPortalURL },
    { key: 'trading', to: tradingPortalURL },
    { key: 'marketing', to: marketingPortalURL },
  ];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isGranted = checkGranted(permissions);

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <div
      className="flex justify-end items-center border-0 border-l border-solid border-grey-100 py-0 px-4"
      data-testid="header-user-settings"
    >
      <button
        data-testid="header-user-settings-menu"
        className="flex appearance-none bg-transparent border-none cursor-pointer p-0"
        onClick={handleOpen}
      >
        <ProfileIcon />
      </button>
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={handleClose}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '280px',
              minWidth: 'fit-content',
              marginTop: '1.5rem',
            },
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
      >
        <MenuItem className="relative flex h-12 w-full items-center px-4" disabled>
          <Typography variant="body2">{t('SHARED_ACCOUNT', { email })}</Typography>
        </MenuItem>
        {(isGranted(PermissionId.configureUsers) || isGranted(PermissionId.configureAdmins)) && (
          <MenuItem
            data-testid="header-user-settings-menu-user-management"
            className="menuItemStyling"
            component={Link}
            to={userManagement.getLink()}
          >
            <Typography variant="body2">{t('HEADER_MENU_USER_MANAGEMENT')}</Typography>
            <div className="absolute -top-px right-0 h-px w-[95%] bg-grey-200" />
          </MenuItem>
        )}
        {(salesPartner || !!salesPartners?.length) && (
          <MenuItem
            data-testid="header-user-settings-menu-sales-partner-settings"
            className="menuItemStyling"
            component={Link}
            to={salesPartnerSettings.getLink()}
          >
            <Typography variant="body2">{t('HEADER_MENU_SALES_PARTNER_SETTINGS')}</Typography>
            <div className="absolute -top-px right-0 h-px w-[95%] bg-grey-200" />
          </MenuItem>
        )}
        {roles.has(UserRole.VPPOperator) &&
          portalLinks.map(({ key, to }) => (
            <MenuItem
              key={key}
              data-testid={`header-user-settings-menu-${key}-portal`}
              className="menuItemStyling"
              component={Link}
              to={to}
              target="_blank"
            >
              <Typography variant="body2">{t(`HEADER_MENU_${key.toUpperCase()}_PORTAL`)}</Typography>
              <ArrowRightIcon />
              <div className="absolute -top-px right-0 h-px w-[95%] bg-grey-200" />
            </MenuItem>
          ))}
        <MenuItem data-testid="header-user-settings-menu-logout" className="menuItemStyling" onClick={logout}>
          <LogoutIcon className="mr-1 w-5" />
          <Typography variant="body2">{t('HEADER_MENU_LOGOUT')}</Typography>
          <div className="absolute -top-px right-0 h-px w-[95%] bg-grey-200" />
        </MenuItem>
      </Menu>
    </div>
  );
};
